/* eslint-disable react-refresh/only-export-components --
   Widget module: the FormWidget descriptor (checkpointCheckerWidget) and its renderer
   component are one cohesive plugin registration — the renderer is reached only
   through the descriptor. Splitting the renderer into a sibling file for a
   dev-HMR benefit fragments the widget with no reuse gain. */
import { useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import { FormItemLabel } from "../input/form-item-label";
import { FormWidget, FormWidgetProps, FormWidgetService } from "./form-widget-service";
import { adminRpc } from "../../../services/admin-rpc-client";
import { monitorPluginsOfFileset, RegisteredPlugin } from "../monitor-plugin-config";

// The composite value this widget owns: the monitor's plugin_fileset_uid and the plugin's instance name
// (plugin_name). Both null = no plugin picked: the form writes runMonitor of system-healthchecks, a plain
// job's run (FEATURE-20261025-unify-the-checker-pattern: every monitor names its plugin).
export interface CheckerValue {
  plugin_fileset_uid: string | null;
  plugin: string | null;
}

// A checkpoint fileset row for the picker, with the plugins its last push registered.
interface FilesetRow {
  uid: string;
  name: string;
  current_revision: string | null;
  extra_data?: unknown;
}

const DEFAULT_OPTION = "__default__";

const CheckpointCheckerWidget: React.FC<FormWidgetProps> = ({ formItem, formData, setValue }) => {
  const current = (formData[formItem.name] as CheckerValue | undefined) ?? {
    plugin_fileset_uid: null,
    plugin: null,
  };

  const [filesets, setFilesets] = useState<FilesetRow[]>([]);

  // Load the fileset list once, with each one's plugin registration.
  useEffect(() => {
    let cancelled = false;
    adminRpc
      .query("SELECT uid, name, current_revision, extra_data FROM fileset WHERE status = 'active' ORDER BY name")
      .then((resp) => {
        if (!cancelled) setFilesets(resp.rows as unknown as FilesetRow[]);
      })
      .catch(() => {
        if (!cancelled) setFilesets([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedFilesetUid = current.plugin_fileset_uid ?? "";

  // The chosen fileset's monitor plugins, as its push registered them (fileset.extra_data.pluginDecls).
  const plugins: RegisteredPlugin[] = useMemo(() => {
    const fileset = filesets.find((f) => f.uid === selectedFilesetUid);
    return fileset ? monitorPluginsOfFileset(fileset) : [];
  }, [filesets, selectedFilesetUid]);

  const onFilesetChange = (uid: string) => {
    if (uid === DEFAULT_OPTION || uid === "") {
      // No plugin named: the row is judged as a run.
      setValue({ plugin_fileset_uid: null, plugin: null } as CheckerValue);
    } else {
      // New fileset → drop any stale plugin until one is picked.
      setValue({ plugin_fileset_uid: uid, plugin: null } as CheckerValue);
    }
  };

  const onPluginChange = (name: string) => {
    setValue({ plugin_fileset_uid: current.plugin_fileset_uid, plugin: name || null } as CheckerValue);
  };

  const selectedFileset = filesets.find((f) => f.uid === selectedFilesetUid);

  return (
    <div className="_checkpoint-checker-widget">
      <TextField
        select
        fullWidth
        variant="outlined"
        value={selectedFilesetUid || DEFAULT_OPTION}
        onChange={(e) => onFilesetChange(e.target.value)}
        slotProps={{ select: { MenuProps: { disableScrollLock: true } } }}
        label={
          <FormItemLabel
            label={formItem.label ?? "Monitor plugin's fileset"}
            helpText={
              formItem.helpText ??
              "Which checkpoint fileset holds the plugin that judges this monitor. Default = judged as a run by system-healthchecks' runMonitor."
            }
          />
        }
      >
        <MenuItem value={DEFAULT_OPTION}>— default (a run, judged by runMonitor) —</MenuItem>
        {filesets.map((fs) => (
          <MenuItem key={fs.uid} value={fs.uid}>
            {fs.name}
          </MenuItem>
        ))}
      </TextField>

      {selectedFilesetUid && (
        <TextField
          select
          fullWidth
          variant="outlined"
          sx={{ mt: 2 }}
          value={current.plugin ?? ""}
          onChange={(e) => onPluginChange(e.target.value)}
          slotProps={{ select: { MenuProps: { disableScrollLock: true } } }}
          helperText={
            !selectedFileset?.current_revision
              ? "this fileset has no pushed revision"
              : plugins.length === 0
                ? "this fileset registers no monitor plugin (an instance of MonitorPlugin); push it once so its plugins are registered"
                : undefined
          }
          label={<FormItemLabel label="Monitor plugin" />}
        >
          {plugins.map((p) => (
            <MenuItem key={p.name} value={p.name}>
              {p.title ? `${p.title} (${p.name})` : p.name}
            </MenuItem>
          ))}
        </TextField>
      )}
    </div>
  );
};

export const checkpointCheckerWidget: FormWidget = {
  name: "checkpoint-checker",
  render: (props) => <CheckpointCheckerWidget {...props} />,
};

// Self-register on import (mirrors how form layouts register themselves).
FormWidgetService.get().register(checkpointCheckerWidget);
